import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "../../icon";
import { cn } from "../../utils";

/**
 * PulseClaims — the ◎ overlay (the binding seam made visible; docs/design/
 * PULSE-SPINE-PRD.md §4.3). A committed objective "rides" a wall cluster when one
 * of its key results binds that touchpoint (`kr.signal_ref`, v91). This marks each
 * claimed cluster with a ◎ so a surface shows only the bets riding ITS bars —
 * objectives are app-wide-sourced but locally relevant, not a repeated strip.
 *
 * Like `PulseSynthesis`, it wraps the wall and finds clusters by the tags the wall
 * already renders (`data-pulse-cluster` for a mode wall's sections,
 * `data-pulse-mode` for the home's mode clusters), so it works for BOTH wall
 * implementations with ZERO changes to either. The wall loads async (SWR), so it
 * re-measures on resize, on DOM mutation, and on a short settle schedule.
 */

export interface PulseClaimMark {
  /** The cluster key — a section `group.key` (mode wall) or a mode slug (home). */
  key: string;
  /** The bet that rides it (shown on the ◎'s tooltip). */
  label: string;
}

const esc = (s: string) => (typeof CSS !== "undefined" && CSS.escape ? CSS.escape(s) : s);

export function PulseClaims({
  claims,
  children,
  className,
}: {
  claims: PulseClaimMark[];
  children: ReactNode;
  className?: string;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [marks, setMarks] = useState<{ x: number; y: number; label: string }[]>([]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || claims.length === 0) {
      setMarks([]);
      return;
    }
    const measure = () => {
      const sr = stage.getBoundingClientRect();
      const next: { x: number; y: number; label: string }[] = [];
      for (const c of claims) {
        const el =
          stage.querySelector<HTMLElement>(`[data-pulse-cluster="${esc(c.key)}"]`) ??
          stage.querySelector<HTMLElement>(`[data-pulse-mode="${esc(c.key)}"]`);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        // top-right corner of the claimed cluster
        next.push({ x: r.right - sr.left - 9, y: r.top - sr.top + 9, label: c.label });
      }
      setMarks(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    // The wall mounts its bars after the SWR fetch resolves — re-measure when the
    // subtree changes, plus a short settle schedule as a backstop.
    const mo = new MutationObserver(measure);
    mo.observe(stage, { childList: true, subtree: true });
    const timers = [setTimeout(measure, 300), setTimeout(measure, 900), setTimeout(measure, 1800)];
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", measure);
      timers.forEach(clearTimeout);
    };
  }, [claims]);

  return (
    <div ref={stageRef} className={cn("relative", className)}>
      {children}
      <div className="pointer-events-none absolute inset-0 z-10">
        {marks.map((m, i) => (
          <span
            key={i}
            className="pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-card/80 p-0.5 shadow-sm ring-1 ring-primary/30"
            style={{ left: m.x, top: m.y }}
            title={`Strategic goal rides this: ${m.label}`}
          >
            <Icon name="target" size="xs" className="block text-primary" />
          </span>
        ))}
      </div>
    </div>
  );
}
